// ABC SEO/GEO — GEO / AI Visibility screen. The differentiator: how visible
// the client's business is inside AI answer engines. Violet = the AI accent throughout.
const { Icon } = window.SGIcons;

/* ── shared bits ─────────────────────────────────────────────── */
function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--sg-card)', border: '1px solid var(--sg-border)',
      borderRadius: 'var(--sg-radius-lg)', boxShadow: 'var(--sg-shadow)', ...style }}>{children}</div>
  );
}
function CardHead({ title, sub, ai, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--sg-border)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {ai && <Icon name="sparkles" size={16} color="var(--sg-violet-600)" />}
          <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 16, color: 'var(--sg-text)' }}>{title}</span>
        </div>
        {sub && <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 3 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}
function Delta({ dir, children }) {
  const map = { up: { c: 'var(--sg-green)', bg: 'var(--sg-green-50)', i: 'arrow-up' },
    down: { c: 'var(--sg-red)', bg: 'var(--sg-red-50)', i: 'arrow-down' },
    flat: { c: 'var(--sg-text-2)', bg: 'var(--sg-sunken)', i: 'minus' } };
  const m = map[dir];
  return (
    <span className="sg-tnum" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12.5, fontWeight: 700,
      color: m.c, background: m.bg, padding: '3px 9px 3px 7px', borderRadius: 'var(--sg-radius-pill)' }}>
      <Icon name={m.i} size={13} strokeWidth={2.6} /> {children}
    </span>
  );
}

/* ── KPI cards ───────────────────────────────────────────────── */
function Kpi({ label, value, unit, delta, dir, foot, accent }) {
  return (
    <Card style={{ padding: 18, animation: 'sg-rise 0.4s var(--sg-ease) both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}>{label}</span>
        {accent && <Icon name="sparkles" size={15} color="var(--sg-violet-600)" />}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 36,
          letterSpacing: '-0.02em', color: accent ? 'var(--sg-violet-700)' : 'var(--sg-text)', lineHeight: 1 }}>{value}</span>
        {unit && <span className="sg-tnum" style={{ fontSize: 16, fontWeight: 600, color: 'var(--sg-text-3)' }}>{unit}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
        <Delta dir={dir}>{delta}</Delta>
        <span style={{ fontSize: 12, color: 'var(--sg-text-3)' }}>{foot}</span>
      </div>
    </Card>
  );
}

/* ── engine breakdown ────────────────────────────────────────── */
function EngineRow({ e, i, last }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr 92px', alignItems: 'center', gap: 16, padding: '13px 20px',
      borderBottom: !last ? '1px solid var(--sg-border)' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
        <span style={{ width: 30, height: 30, borderRadius: 9, background: e.color, flex: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14,
          fontFamily: 'var(--sg-font-display)' }}>{e.tag}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
          <div className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)' }}>{e.mentions.toLocaleString()} mentions</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--sg-violet-50)', overflow: 'hidden' }}>
          <div style={{ width: e.vis + '%', height: '100%', borderRadius: 999, background: 'var(--sg-grad-violet)',
            transformOrigin: 'left', animation: 'sg-grow 0.7s var(--sg-ease) both', animationDelay: (0.1 + i * 0.07) + 's' }} />
        </div>
        <span className="sg-tnum" style={{ width: 40, textAlign: 'right', fontSize: 13.5, fontWeight: 700, color: 'var(--sg-violet-700)' }}>{e.vis}%</span>
      </div>
      <div style={{ textAlign: 'right' }}>{e.delta ? <Delta dir={e.dir}>{e.delta.replace(/[+−-]/, '')}</Delta> : <span style={{ fontSize: 12, color: 'var(--sg-text-3)' }}>—</span>}</div>
    </div>
  );
}

/* ── prompts table ───────────────────────────────────────────── */
const SENT = {
  Positive: { c: 'var(--sg-green)', bg: 'var(--sg-green-50)' },
  Neutral: { c: 'var(--sg-text-2)', bg: 'var(--sg-sunken)' },
  Mixed: { c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)' },
  Missing: { c: 'var(--sg-red)', bg: 'var(--sg-red-50)' },
};
function PromptRow({ r, i, last }) {
  const s = SENT[r.sent];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 64px 96px', alignItems: 'center', gap: 14, padding: '13px 20px',
      borderBottom: !last ? '1px solid var(--sg-border)' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        <Icon name="message-square" size={15} color="var(--sg-text-3)" />
        <span style={{ fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{r.q}"</span>
      </div>
      <div style={{ display: 'flex', gap: -4 }}>
        {r.engines.length === 0
          ? <span style={{ fontSize: 12, color: 'var(--sg-text-3)' }}>Not cited</span>
          : r.engines.map((c, k) => (
            <span key={k} style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: '2px solid var(--sg-card)',
              marginLeft: k ? -6 : 0 }} />
          ))}
      </div>
      <span className="sg-tnum" style={{ fontSize: 13.5, fontWeight: 700, color: r.pos === '—' ? 'var(--sg-text-3)' : 'var(--sg-text)' }}>{r.pos}</span>
      <span style={{ justifySelf: 'start', fontSize: 11.5, fontWeight: 700, color: s.c, background: s.bg, padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>{r.sent}</span>
    </div>
  );
}

/* ── next actions ────────────────────────────────────────────── */
const ACTIONS = [
  { t: 'Add a clear FAQ page answering customer questions', d: 'AI assistants pull direct, citable answers straight from FAQ content.', impact: 'High' },
  { t: 'Publish a “best options” comparison page for your niche', d: 'These pages are exactly what AI engines cite when asked for a recommendation.', impact: 'High' },
  { t: 'Earn more cited reviews and third-party mentions', d: 'Trust signals AI engines read before they recommend you.', impact: 'Medium' },
];
function ActionItem({ a, i }) {
  const [hover, setHover] = React.useState(false);
  const hi = a.impact === 'High';
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 'var(--sg-radius-md)',
        background: hover ? 'var(--sg-violet-50)' : 'transparent', cursor: 'pointer', transition: 'background 140ms var(--sg-ease)' }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--sg-violet-100)', color: 'var(--sg-violet-700)', fontWeight: 700, fontSize: 13, fontFamily: 'var(--sg-font-display)' }}>{i + 1}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)' }}>{a.t}</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: hi ? 'var(--sg-violet-700)' : 'var(--sg-text-2)',
            background: hi ? 'var(--sg-violet-100)' : 'var(--sg-sunken)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)' }}>{a.impact} impact</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 3, lineHeight: 1.45 }}>{a.d}</div>
      </div>
      <Icon name="chevron-right" size={18} color={hover ? 'var(--sg-violet-600)' : 'var(--sg-text-3)'} style={{ alignSelf: 'center' }} />
    </div>
  );
}

/* ── screen (LIVE — scored via Cloudflare Workers AI) ─────────── */
const ENGINE_META = {
  chatgpt: { name: 'ChatGPT', tag: 'C', color: '#10A37F' },
  gemini: { name: 'Gemini', tag: 'G', color: '#8E75F0' },
  perplexity: { name: 'Perplexity', tag: 'P', color: '#20808D' },
};

/* ── Topic ownership ──────────────────────────────────────────
   A share-of-voice percentage on its own invites "is that good?" — the answer
   depends entirely on what competitors are doing. This states the position in
   words, using the thresholds from Semrush's ChatGPT topic-authority study so
   "owner" means the same thing here as it does in their research.

   Unsettled is deliberately styled as an OPPORTUNITY, not a failure. In that
   study 53.7% of categories had no owner at all, and leaders held position in
   90.4% of month-over-month checks — so an open category is the cheapest thing
   a local business can win, and the most urgent. Painting it red would tell
   exactly the wrong story. */
const OWN_META = {
  owner:     { label: 'You own this topic',    tone: 'var(--sg-green)',      bg: 'var(--sg-green-50)',  icon: 'sparkles' },
  emerging:  { label: 'Emerging leader',       tone: 'var(--sg-violet-600)', bg: 'var(--sg-violet-50)', icon: 'sparkles' },
  unsettled: { label: 'Category up for grabs', tone: 'var(--sg-amber)',      bg: 'var(--sg-amber-50)',  icon: 'sparkles' },
};

function OwnershipCard({ own }) {
  if (!own) return null;
  const meta = OWN_META[own.status] || OWN_META.unsettled;
  const showLead = own.leadMargin != null && own.topRival;

  return (
    <Card style={{ padding: 0, overflow: 'hidden', animation: 'sg-rise 0.4s var(--sg-ease) both' }}>
      <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 420px', padding: '20px 22px', borderLeft: `4px solid ${meta.tone}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: meta.tone, background: meta.bg, padding: '4px 11px', borderRadius: 'var(--sg-radius-pill)',
            }}>{meta.label}</span>
            {own.defensible === true && (
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '4px 10px', borderRadius: 'var(--sg-radius-pill)' }}>
                Defensible lead
              </span>
            )}
            {own.defensible === false && (
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-amber)', background: 'var(--sg-amber-50)', padding: '4px 10px', borderRadius: 'var(--sg-radius-pill)' }}>
                Thin lead — vulnerable
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: 'var(--sg-text-2)' }}>{own.explain}</p>
        </div>

        {/* Wraps on purpose. Three stats at their natural width come to ~407px,
            which overflowed a 375px phone — and because the page itself does not
            scroll horizontally, the third one (the lead margin, the most
            actionable number here) was being silently clipped off rather than
            pushed somewhere reachable. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', flex: '1 1 320px', borderLeft: '1px solid var(--sg-border)' }}>
          <OwnStat label="Brand mentions" value={own.brandShare + '%'} foot="share vs rivals" />
          <OwnStat label="Questions named in" value={own.promptsNamed + '/' + own.promptsTotal} foot={own.coverageRate + '% coverage'} />
          <OwnStat
            label={showLead ? 'Lead vs ' + own.topRival : 'Lead'}
            value={showLead ? (own.leadMargin > 0 ? '+' : '') + own.leadMargin + 'pp' : '—'}
            foot={showLead ? (own.leadMargin > 0 ? 'ahead' : 'behind') : 'no rival named'}
            tone={showLead ? (own.leadMargin > 0 ? 'var(--sg-green)' : 'var(--sg-red)') : undefined}
          />
        </div>
      </div>
    </Card>
  );
}

function OwnStat({ label, value, foot, tone }) {
  return (
    <div style={{ padding: '20px 22px', flex: '1 1 auto', minWidth: 118 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sg-text-2)', marginBottom: 7 }}>{label}</div>
      <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 24, letterSpacing: '-0.02em', color: tone || 'var(--sg-text)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--sg-text-2)', marginTop: 5 }}>{foot}</div>
    </div>
  );
}

function GeoVisibility() {
  // Scope everything to the currently-selected domain so a multi-domain account
  // shows each site's AI visibility independently (no cross-domain bleed).
  const { site, prettySite } = window.SGLive.useLive();
  const domain = site ? prettySite(site) : '';
  const g = window.SGLive.useGeo(domain);

  const me = g.me, summary = g.summary;
  // Scanned state + score are now PER-DOMAIN (from the domain-scoped summary),
  // so switching the active site changes everything — no client-wide bleed.
  const loadingSummary = summary === null;              // useGeo nulls summary on domain change, then refetches
  const scanned = !!(summary && summary.checked_at);
  const biz = domain || (me && (me.business_name || me.domain)) || 'your business';

  const [mentions, setMentions] = React.useState(null);
  React.useEffect(() => { setMentions(null); if (scanned) window.SGLive.fetchGeoMentions(domain).then(setMentions); }, [scanned, domain]);

  // engines from the stored by-platform summary
  const engines = (summary && summary.by_platform)
    ? Object.keys(ENGINE_META).filter((p) => summary.by_platform[p]).map((p) => {
        const bp = summary.by_platform[p];
        return { ...ENGINE_META[p], vis: bp.total ? Math.round((bp.mentioned / bp.total) * 100) : 0, mentions: bp.mentioned };
      })
    : [];

  const sov = summary && summary.total ? Math.round((summary.mentioned / summary.total) * 100) : 0;
  const totalMentions = summary ? summary.mentioned : 0;

  // prompts grouped by query, with which engines cited + best position + sentiment
  let prompts = [];
  let avgPos = null;
  if (mentions && mentions.length) {
    const byQ = {};
    const posAll = [];
    mentions.forEach((m) => {
      if (!byQ[m.query]) byQ[m.query] = { q: m.query, colors: [], positions: [], sentiments: [] };
      if (m.mentioned) {
        const meta = ENGINE_META[m.platform];
        if (meta) byQ[m.query].colors.push(meta.color);
        if (m.position) { byQ[m.query].positions.push(m.position); posAll.push(m.position); }
        if (m.sentiment) byQ[m.query].sentiments.push(m.sentiment);
      }
    });
    if (posAll.length) avgPos = (posAll.reduce((a, b) => a + b, 0) / posAll.length);
    prompts = Object.values(byQ).map((o) => {
      const cited = o.colors.length > 0;
      const sent = !cited ? 'Missing' : o.sentiments.includes('positive') ? 'Positive' : o.sentiments.includes('negative') ? 'Mixed' : 'Neutral';
      return { q: o.q, engines: o.colors, pos: o.positions.length ? '#' + Math.min(...o.positions) : '—', sent };
    }).sort((a, b) => b.engines.length - a.engines.length).slice(0, 8);
  }

  const Header = (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--sg-violet-600)', marginBottom: 6 }}>The differentiator</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--sg-grad-violet)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(124,58,237,0.35)' }}>
            <Icon name="sparkles" size={20} color="#fff" />
          </span>
          <h1 style={{ margin: 0, fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 28, letterSpacing: '-0.02em', color: 'var(--sg-text)' }}>GEO / AI Visibility</h1>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 14, color: 'var(--sg-text-2)', maxWidth: 640, lineHeight: 1.5 }}>
          How often AI assistants mention <strong style={{ color: 'var(--sg-text)' }}>{biz}</strong> when people ask. Scored live across multiple AI models — higher visibility means more customers find you inside ChatGPT, Perplexity, Gemini & more.
        </p>
      </div>
      <button onClick={g.scan} disabled={g.scanning}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 42, padding: '0 18px', border: 'none',
          borderRadius: 'var(--sg-radius-pill)', cursor: g.scanning ? 'wait' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14,
          color: '#fff', background: 'var(--sg-grad-violet)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}>
        <Icon name={g.scanning ? 'refresh-cw' : 'sparkles'} size={16} /> {g.scanning ? 'Scanning AI engines…' : scanned ? 'Re-run AI scan' : 'Run AI check'}
      </button>
    </div>
  );

  // While the domain-scoped summary is still loading, hold the previous frame
  // shape (spinner) instead of flashing the "run first scan" empty state.
  if (loadingSummary) {
    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {Header}
        <Card style={{ padding: '64px 24px', textAlign: 'center' }}>
          <div style={{ width: 30, height: 30, margin: '0 auto', border: '3px solid var(--sg-border)', borderTopColor: 'var(--sg-violet-600)', borderRadius: '50%', animation: 'sg-spin 0.8s linear infinite' }} />
          <div style={{ marginTop: 14, fontSize: 13.5, color: 'var(--sg-text-2)' }}>Loading AI visibility for {biz}…</div>
        </Card>
      </div>
    );
  }

  if (!scanned) {
    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {Header}
        <Card style={{ padding: '64px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 64, height: 64, borderRadius: 'var(--sg-radius-xl)', background: 'var(--sg-violet-50)', color: 'var(--sg-violet-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="sparkles" size={30} />
          </span>
          <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 22, color: 'var(--sg-text)' }}>See how AI assistants describe you</div>
          <div style={{ fontSize: 14, color: 'var(--sg-text-2)', maxWidth: 460, lineHeight: 1.55 }}>
            We’ll ask ChatGPT-, Gemini- and Perplexity-class models the questions your customers ask, and measure how often you’re mentioned. Takes about 30 seconds.
          </div>
          <button onClick={g.scan} disabled={g.scanning}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 24px', marginTop: 6, border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: g.scanning ? 'wait' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14.5, color: '#fff', background: 'var(--sg-grad-violet)', boxShadow: '0 4px 16px rgba(124,58,237,0.35)' }}>
            <Icon name="sparkles" size={17} /> {g.scanning ? 'Scanning AI engines…' : 'Run my first AI-visibility scan'}
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {Header}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <Kpi label="AI Visibility Score" value={summary ? summary.score : 0} unit="/100" delta="live" dir="flat" foot="across AI models" accent />
        <Kpi label="AI mentions" value={totalMentions.toLocaleString()} delta="live" dir="flat" foot={`of ${summary ? summary.total : 0} queries`} />
        <Kpi label="Share of AI answers" value={sov} unit="%" delta="live" dir="flat" foot="you're cited in" />
        <Kpi label="Avg. answer position" value={avgPos != null ? '#' + avgPos.toFixed(1) : '—'} delta="live" dir="flat" foot="when cited" />
      </div>

      {/* Where the client actually stands — above the engine detail, because
          "do I own this category?" is the question the numbers are answering. */}
      <OwnershipCard own={summary && summary.topic_ownership} />

      {/* engines + honest trend */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 20, alignItems: 'start' }}>
        <Card>
          <CardHead ai title="How AI engines see you" sub="Share of your prompts where each model cites you"
            right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
          {engines.length ? <div>{engines.map((e, i) => <EngineRow key={e.name} e={e} i={i} last={i === engines.length - 1} />)}</div>
            : <div style={{ padding: 24, color: 'var(--sg-text-2)', fontSize: 13 }}>No engine data yet.</div>}
        </Card>
        <Card>
          <CardHead ai title="How it's measured" />
          <div style={{ padding: 20, fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.6 }}>
            <p style={{ margin: '0 0 12px' }}>Your score blends how often you’re mentioned, across how many models, and the sentiment of those mentions.</p>
            <p style={{ margin: 0 }}>Measured via open models on Cloudflare Workers AI. Connect ChatGPT / Perplexity API keys to score those exact engines. A visibility trend appears as you re-scan over time.</p>
          </div>
        </Card>
      </div>

      {/* prompts + actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 20, alignItems: 'start' }}>
        <Card>
          <CardHead title="Prompts where you show up" sub="The real questions we asked the AI models"
            right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 64px 96px', gap: 14, padding: '10px 20px', borderBottom: '1px solid var(--sg-border)',
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>
            <span>Prompt</span><span>Cited in</span><span>Position</span><span>Sentiment</span>
          </div>
          {prompts.length ? <div>{prompts.map((r, i) => <PromptRow key={i} r={r} i={i} last={i === prompts.length - 1} />)}</div>
            : <div style={{ padding: 24, color: 'var(--sg-text-2)', fontSize: 13 }}>Loading prompts…</div>}
        </Card>

        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--sg-border)', background: 'var(--sg-violet-50)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="zap" size={16} color="var(--sg-violet-600)" />
              <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 16, color: 'var(--sg-text)' }}>What to do next</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 3 }}>Plain-English moves that lift your AI visibility fastest.</div>
          </div>
          <div style={{ padding: '8px 6px' }}>{ACTIONS.map((a, i) => <ActionItem key={i} a={a} i={i} />)}</div>
        </Card>
      </div>
    </div>
  );
}

window.SGGeoVisibility = GeoVisibility;
// Exposed alongside the screen so the ownership card can be rendered on its own
// — it is the piece that ends up on client reports, and it needs to be checkable
// in all three states without standing up an authenticated session with live
// GEO data behind it.
window.SGOwnershipCard = OwnershipCard;
